import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { normLang } from "@/i18n";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  DollarSign, Save, Factory, Layers3, Calculator, History, Search, RefreshCw, Info,
  Globe2, Eye, CheckCircle2, AlertTriangle, Loader2, ShieldAlert,
} from "lucide-react";

const money = (n, cur = "USD") => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`);

// Full bilingual UI dictionary for this page (FR/EN). Business logic and values are untouched.
const TXT = {
  fr: {
    title: "Tarification",
    subtitle: "Prix client = coût fournisseur × multiplicateur. Priorité : prix fixe service › mult. service › mult. fournisseur › mult. global.",
    globalCfg: "Configuration globale",
    currency: "Devise", defaultMult: "défaut",
    globalMult: "Multiplicateur global",
    saveApply: "Enregistrer & appliquer",
    globalInfo: "Applique le recalcul aux services concernés uniquement. Les commandes passées ne changent jamais.",
    byProvider: "Par fournisseur",
    provider: "Fournisseur", services: "Services", multiplier: "Multiplicateur", action: "Action",
    noProvider: "Aucun fournisseur.",
    byService: "Par service",
    searchService: "Rechercher un service",
    service: "Service", cost: "Coût", effMult: "Mult. effectif", clientPrice: "Prix client", source: "Source", override: "Override",
    noService: "Aucun service.",
    servicesShown: (total) => `${total} services · 25 affichés`,
    simulator: "Simulateur de prix (informatif — ne modifie rien)",
    serviceIdOpt: "Service (id, optionnel)",
    providerCost: "Coût fournisseur ($/1000)",
    newMult: "Nouveau multiplicateur",
    simulate: "Simuler",
    currentPrice: "Prix actuel", newPriceSim: "Nouveau prix (simulé)",
    totalUnits: (q, m) => `Total ${q} u. : ${m}`,
    historyTitle: "Historique des modifications",
    field: "Champ", before: "Avant", after: "Après", by: "Par", date: "Date",
    noHistory: "Aucune modification enregistrée.",
    inherit: "hérite global", apply: "Appliquer", reset: "Réinit.",
    multService: "Multiplicateur service", fixedPrice: "Prix fixe /1000", mult: "mult", fixed: "fixe $",
    // toasts
    tGlobalDone: (m, c) => `Multiplicateur global ×${m} appliqué (${c} services mis à jour)`,
    tProviderDone: (c) => `Fournisseur mis à jour (${c} services recalculés)`,
    tPriceDone: (p) => `Prix recalculé : ${p}`,
    tNoChange: "Aucun changement de prix",
    // source labels
    src: {
      FIXED_SERVICE: "Prix fixe service", SERVICE_MULT: "Mult. service", PROVIDER_MULT: "Mult. fournisseur",
      GLOBAL_MULT: "Mult. global", FALLBACK_GLOBAL: "Fallback", FALLBACK_LEGACY: "Legacy (coût absent)",
      LEGACY_MATERIALISED: "Legacy matérialisé", LEGACY: "Legacy",
    },
    // catalog apply
    catTitle: "Appliquer à tout le catalogue",
    catIntro: "Recalcule les price_per_1000 du catalogue depuis le Pricing Engine centralisé. Une prévisualisation obligatoire précède toute écriture.",
    catWarn: "Cette opération modifie les prix catalogue futurs. Les commandes historiques ne sont pas modifiées.",
    catPreview: "Prévisualiser les changements", catPreviewing: "Analyse en cours…",
    catAnalyzed: "Services analysés", catWillChange: "Prix qui changeront", catUnchanged: "Prix inchangés", catBlocked: "Services bloqués",
    catExamples: "Exemples (avant → après)", catBlockedList: "Services bloqués (prix conservé)",
    catConfirm: "Confirmer l'application", catApplying: "Application…",
    catNoChange: "Aucun changement à appliquer — le catalogue est déjà aligné.",
    catReason: "Raison (optionnel)",
    catDone: (c, u, b) => `Appliqué : ${c} modifiés · ${u} inchangés · ${b} bloqués`,
    catReload: "Rafraîchir l'aperçu", catNone: "—",
  },
  en: {
    title: "Pricing",
    subtitle: "Client price = provider cost × multiplier. Priority: fixed service price › service mult. › provider mult. › global mult.",
    globalCfg: "Global configuration",
    currency: "Currency", defaultMult: "default",
    globalMult: "Global multiplier",
    saveApply: "Save & apply",
    globalInfo: "Recomputes only the affected services. Past orders are never changed.",
    byProvider: "Per provider",
    provider: "Provider", services: "Services", multiplier: "Multiplier", action: "Action",
    noProvider: "No provider.",
    byService: "Per service",
    searchService: "Search a service",
    service: "Service", cost: "Cost", effMult: "Effective mult.", clientPrice: "Client price", source: "Source", override: "Override",
    noService: "No service.",
    servicesShown: (total) => `${total} services · 25 shown`,
    simulator: "Price simulator (informational — changes nothing)",
    serviceIdOpt: "Service (id, optional)",
    providerCost: "Provider cost ($/1000)",
    newMult: "New multiplier",
    simulate: "Simulate",
    currentPrice: "Current price", newPriceSim: "New price (simulated)",
    totalUnits: (q, m) => `Total ${q} u.: ${m}`,
    historyTitle: "Change history",
    field: "Field", before: "Before", after: "After", by: "By", date: "Date",
    noHistory: "No change recorded.",
    inherit: "inherit global", apply: "Apply", reset: "Reset",
    multService: "Service multiplier", fixedPrice: "Fixed price /1000", mult: "mult", fixed: "fixed $",
    tGlobalDone: (m, c) => `Global multiplier ×${m} applied (${c} services updated)`,
    tProviderDone: (c) => `Provider updated (${c} services recomputed)`,
    tPriceDone: (p) => `Price recomputed: ${p}`,
    tNoChange: "No price change",
    src: {
      FIXED_SERVICE: "Fixed service price", SERVICE_MULT: "Service mult.", PROVIDER_MULT: "Provider mult.",
      GLOBAL_MULT: "Global mult.", FALLBACK_GLOBAL: "Fallback", FALLBACK_LEGACY: "Legacy (missing cost)",
      LEGACY_MATERIALISED: "Materialised legacy", LEGACY: "Legacy",
    },
    catTitle: "Apply to whole catalog",
    catIntro: "Recompute catalog price_per_1000 from the central Pricing Engine. A mandatory preview precedes any write.",
    catWarn: "This operation changes future catalog prices. Historical orders are not modified.",
    catPreview: "Preview changes", catPreviewing: "Analyzing…",
    catAnalyzed: "Services analyzed", catWillChange: "Prices that will change", catUnchanged: "Prices unchanged", catBlocked: "Blocked services",
    catExamples: "Examples (before → after)", catBlockedList: "Blocked services (price preserved)",
    catConfirm: "Confirm apply", catApplying: "Applying…",
    catNoChange: "Nothing to apply — the catalog is already aligned.",
    catReason: "Reason (optional)",
    catDone: (c, u, b) => `Applied: ${c} changed · ${u} unchanged · ${b} blocked`,
    catReload: "Refresh preview", catNone: "—",
  },
};

const useT = () => {
  const { i18n } = useTranslation();
  return TXT[normLang(i18n.language)] || TXT.fr;
};

const SOURCE_TONE = {
  FIXED_SERVICE: "bg-amber-500/15 text-amber-600", SERVICE_MULT: "bg-blue-500/15 text-blue-600",
  PROVIDER_MULT: "bg-violet-500/15 text-violet-600", GLOBAL_MULT: "bg-emerald-500/15 text-emerald-600",
};
const SourceBadge = ({ s, L }) => (
  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${SOURCE_TONE[s] || "bg-slate-500/15 text-slate-500"}`}>{L.src[s] || s}</span>
);

function Section({ title, icon: Icon, children, right, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-border bg-card p-4 sm:p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold"><Icon className="h-4 w-4 text-accent" /> {title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

export default function AdminPricing() {
  const L = useT();
  const [cfg, setCfg] = useState(null);
  const [globalMult, setGlobalMult] = useState("");
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState("");
  const [sim, setSim] = useState({ service_id: "", supplier_cost: "", multiplier: "", quantity: 1000 });
  const [simRes, setSimRes] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadCfg = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/pricing");
      setCfg(data); setGlobalMult(String(data.global_multiplier)); setProviders(data.providers || []);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  }, []);
  const loadServices = useCallback(async (q = "") => {
    try { const { data } = await api.get("/admin/pricing/services", { params: { search: q, limit: 25 } }); setServices(data); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  }, []);
  const loadHistory = useCallback(async () => {
    try { const { data } = await api.get("/admin/pricing/history", { params: { limit: 30 } }); setHistory(data.entries || []); }
    catch { /* PRICING_AUDIT_VIEW may be absent */ }
  }, []);

  useEffect(() => { loadCfg(); loadServices(); loadHistory(); }, [loadCfg, loadServices, loadHistory]);

  const saveGlobal = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/pricing/global", { multiplier: parseFloat(globalMult), apply: true });
      toast.success(L.tGlobalDone(data.global_multiplier, data.applied?.changed ?? 0));
      await Promise.all([loadCfg(), loadServices(search), loadHistory()]);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  const saveProvider = async (pid, val) => {
    setBusy(true);
    try {
      const body = val === "" || val == null ? { multiplier: null, apply: true } : { multiplier: parseFloat(val), apply: true };
      const { data } = await api.put(`/admin/pricing/providers/${pid}`, body);
      toast.success(L.tProviderDone(data.applied?.changed ?? 0));
      await Promise.all([loadCfg(), loadServices(search), loadHistory()]);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  const saveService = async (sid, patch) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/pricing/services/${sid}`, patch);
      toast.success(data.changed ? L.tPriceDone(money(data.new_price)) : L.tNoChange);
      await Promise.all([loadServices(search), loadHistory()]);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  const runSimulate = async () => {
    try {
      const body = { quantity: parseInt(sim.quantity || 1000, 10) };
      if (sim.service_id) body.service_id = sim.service_id;
      if (sim.supplier_cost !== "") body.supplier_cost = parseFloat(sim.supplier_cost);
      if (sim.multiplier !== "") body.multiplier = parseFloat(sim.multiplier);
      const { data } = await api.post("/admin/pricing/simulate", body);
      setSimRes(data);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-pricing-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">{L.title}</h1>
        <p className="text-sm text-muted-foreground">{L.subtitle}</p>
      </div>

      {/* Global config */}
      <Section title={L.globalCfg} icon={DollarSign} testid="pricing-global"
        right={cfg && <span className="text-xs text-muted-foreground">{L.currency} : {cfg.currency} · {L.defaultMult} ×{cfg.default_multiplier}</span>}>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">{L.globalMult}</label>
            <div className="flex items-center gap-1">
              <span className="text-lg font-bold">×</span>
              <input data-testid="pricing-global-input" type="number" step="0.01" min="0" value={globalMult}
                onChange={(e) => setGlobalMult(e.target.value)}
                className="w-28 rounded-lg border border-border bg-background px-3 py-2 text-sm" />
            </div>
          </div>
          <button data-testid="pricing-global-save" onClick={saveGlobal} disabled={busy}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
            <Save className="h-4 w-4" /> {L.saveApply}
          </button>
          <div className="flex items-start gap-1.5 text-xs text-muted-foreground"><Info className="mt-0.5 h-3.5 w-3.5" /> {L.globalInfo}</div>
        </div>
      </Section>

      {/* Providers */}
      <Section title={L.byProvider} icon={Factory} testid="pricing-providers">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-muted-foreground"><th className="py-2">{L.provider}</th><th>{L.services}</th><th>{L.multiplier}</th><th>{L.action}</th></tr></thead>
            <tbody>
              {providers.map((p) => <ProviderRow key={p.id} p={p} onSave={saveProvider} busy={busy} L={L} />)}
              {!providers.length && <tr><td colSpan={4} className="py-4 text-muted-foreground">{L.noProvider}</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Services */}
      <Section title={L.byService} icon={Layers3} testid="pricing-services"
        right={
          <div className="flex items-center gap-2">
            <div className="relative"><Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <input data-testid="pricing-service-search" value={search} onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadServices(search)} placeholder={L.searchService}
                className="w-48 rounded-lg border border-border bg-background py-1.5 pl-7 pr-2 text-sm" /></div>
            <button onClick={() => loadServices(search)} className="rounded-lg border border-border p-2 hover:bg-secondary"><RefreshCw className="h-4 w-4" /></button>
          </div>
        }>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="py-2">{L.service}</th><th>{L.cost}</th><th>{L.effMult}</th><th>{L.clientPrice}</th><th>{L.source}</th><th>{L.override}</th></tr></thead>
            <tbody>
              {services.items.map((s) => <ServiceRow key={s.id} s={s} onSave={saveService} busy={busy} L={L} />)}
              {!services.items.length && <tr><td colSpan={6} className="py-4 text-muted-foreground">{L.noService}</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">{L.servicesShown(services.total)}</div>
      </Section>

      {/* Simulator */}
      <Section title={L.simulator} icon={Calculator} testid="pricing-simulator">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div><label className="mb-1 block text-xs text-muted-foreground">{L.serviceIdOpt}</label>
            <input data-testid="sim-service" value={sim.service_id} onChange={(e) => setSim({ ...sim, service_id: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="service_id" /></div>
          <div><label className="mb-1 block text-xs text-muted-foreground">{L.providerCost}</label>
            <input data-testid="sim-cost" type="number" step="0.0001" value={sim.supplier_cost} onChange={(e) => setSim({ ...sim, supplier_cost: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="0.80" /></div>
          <div><label className="mb-1 block text-xs text-muted-foreground">{L.newMult}</label>
            <input data-testid="sim-mult" type="number" step="0.01" value={sim.multiplier} onChange={(e) => setSim({ ...sim, multiplier: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="3.5" /></div>
          <div className="flex items-end"><button data-testid="sim-run" onClick={runSimulate} className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">{L.simulate}</button></div>
        </div>
        {simRes && (
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="sim-result">
            <div className="rounded-xl border border-border bg-secondary/40 p-3">
              <div className="text-xs text-muted-foreground">{L.currentPrice}</div>
              <div className="font-display text-xl font-bold">{money(simRes.current?.client_unit_price)}</div>
              <div className="mt-1"><SourceBadge s={simRes.current?.pricing_source} L={L} /></div>
            </div>
            <div className="rounded-xl border border-primary/40 bg-primary/5 p-3">
              <div className="text-xs text-muted-foreground">{L.newPriceSim}</div>
              <div className="font-display text-xl font-bold text-primary">{money(simRes.simulated?.client_unit_price)}</div>
              <div className="mt-1 flex items-center gap-2"><SourceBadge s={simRes.simulated?.pricing_source} L={L} /><span className="text-xs text-muted-foreground">{L.totalUnits(simRes.simulated?.requested_quantity, money(simRes.simulated?.gross_for_quantity))}</span></div>
            </div>
          </div>
        )}
      </Section>

      {/* Apply to whole catalog */}
      <CatalogApplySection reloadAll={() => Promise.all([loadCfg(), loadServices(search), loadHistory()])} money={money} L={L} />

      {/* History */}
      <Section title={L.historyTitle} icon={History} testid="pricing-history">
        <div className="max-h-72 overflow-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-muted-foreground"><th className="py-2">{L.field}</th><th>{L.before}</th><th>{L.after}</th><th>{L.by}</th><th>{L.date}</th></tr></thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-t border-border/60">
                  <td className="py-1.5">{h.field}{h.target_id ? ` (${String(h.target_id).slice(0, 8)})` : ""}</td>
                  <td className="text-muted-foreground">{typeof h.before === "object" ? JSON.stringify(h.before) : String(h.before)}</td>
                  <td>{typeof h.after === "object" ? JSON.stringify(h.after).slice(0, 60) : String(h.after)}</td>
                  <td className="text-muted-foreground">{h.user_email || "—"}</td>
                  <td className="text-muted-foreground">{h.created_at ? new Date(h.created_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
              {!history.length && <tr><td colSpan={5} className="py-4 text-muted-foreground">{L.noHistory}</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

function ProviderRow({ p, onSave, busy, L }) {
  const [val, setVal] = useState(p.multiplier ?? "");
  useEffect(() => { setVal(p.multiplier ?? ""); }, [p.multiplier]);
  return (
    <tr className="border-t border-border/60" data-testid={`pricing-provider-${p.id}`}>
      <td className="py-2">{p.name}</td>
      <td className="text-muted-foreground">{p.service_count}</td>
      <td>
        <div className="flex items-center gap-1"><span>×</span>
          <input type="number" step="0.01" min="0" value={val} onChange={(e) => setVal(e.target.value)} placeholder={L.inherit}
            className="w-24 rounded-lg border border-border bg-background px-2 py-1 text-sm" /></div>
      </td>
      <td>
        <button disabled={busy} onClick={() => onSave(p.id, val)} data-testid={`pricing-provider-save-${p.id}`}
          className="rounded-lg border border-border px-3 py-1 text-xs font-semibold hover:bg-secondary disabled:opacity-50">{L.apply}</button>
        {p.multiplier != null && <button disabled={busy} onClick={() => onSave(p.id, "")} className="ml-1 rounded-lg px-2 py-1 text-xs text-muted-foreground hover:bg-secondary">{L.reset}</button>}
      </td>
    </tr>
  );
}

function ServiceRow({ s, onSave, busy, L }) {
  const [mult, setMult] = useState(s.service_multiplier ?? "");
  const [fixed, setFixed] = useState(s.fixed_price_per_1000 ?? "");
  useEffect(() => { setMult(s.service_multiplier ?? ""); setFixed(s.fixed_price_per_1000 ?? ""); }, [s]);
  return (
    <tr className="border-t border-border/60 align-top" data-testid={`pricing-service-${s.id}`}>
      <td className="max-w-[280px] py-2"><div className="truncate" title={s.name}>{s.name}</div></td>
      <td className="text-muted-foreground">{money(s.supplier_cost)}</td>
      <td>{s.effective_multiplier != null ? `×${s.effective_multiplier}` : "—"}</td>
      <td className="font-semibold" data-testid={`pricing-service-price-${s.id}`}>{money(s.client_price)}</td>
      <td><SourceBadge s={s.pricing_source} L={L} /></td>
      <td>
        <div className="flex flex-wrap items-center gap-1">
          <input type="number" step="0.01" value={mult} onChange={(e) => setMult(e.target.value)} placeholder={L.mult}
            className="w-16 rounded border border-border bg-background px-1.5 py-1 text-xs" title={L.multService} />
          <input type="number" step="0.01" value={fixed} onChange={(e) => setFixed(e.target.value)} placeholder={L.fixed}
            className="w-20 rounded border border-border bg-background px-1.5 py-1 text-xs" title={L.fixedPrice} />
          <button disabled={busy} data-testid={`pricing-service-save-${s.id}`}
            onClick={() => onSave(s.id, {
              multiplier: mult === "" ? null : parseFloat(mult), clear_multiplier: mult === "",
              fixed_price_per_1000: fixed === "" ? null : parseFloat(fixed), clear_fixed: fixed === "",
            })}
            className="rounded border border-border px-2 py-1 text-xs font-semibold hover:bg-secondary disabled:opacity-50">OK</button>
        </div>
      </td>
    </tr>
  );
}

function CatalogApplySection({ reloadAll, money, L }) {
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const runPreview = async () => {
    setLoading(true); setPreview(null);
    try {
      const { data } = await api.post("/admin/pricing/catalog/preview");
      setPreview(data);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const runApply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/admin/pricing/catalog/apply", { confirm: true, reason: reason || undefined });
      toast.success(L.catDone(data.changed, data.unchanged, data.blocked));
      setPreview(null); setReason("");
      await reloadAll();
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  const Stat = ({ label, value, tone, testid }) => (
    <div data-testid={testid} className={`rounded-xl border p-3 ${tone}`}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-display text-2xl font-bold">{value}</div>
    </div>
  );

  return (
    <Section title={L.catTitle} icon={Globe2} testid="pricing-catalog-apply">
      <p className="text-sm text-muted-foreground">{L.catIntro}</p>
      <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400" data-testid="pricing-catalog-warning">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{L.catWarn}</span>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button data-testid="pricing-catalog-preview-btn" onClick={runPreview} disabled={loading || applying}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
          {loading ? L.catPreviewing : (preview ? L.catReload : L.catPreview)}
        </button>
      </div>

      {preview && (
        <div className="mt-4 space-y-4" data-testid="pricing-catalog-preview">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={L.catAnalyzed} value={preview.total_analyzed} tone="border-border bg-secondary/40" testid="catalog-stat-analyzed" />
            <Stat label={L.catWillChange} value={preview.would_change} tone="border-primary/40 bg-primary/5 text-primary" testid="catalog-stat-change" />
            <Stat label={L.catUnchanged} value={preview.unchanged} tone="border-border bg-secondary/40" testid="catalog-stat-unchanged" />
            <Stat label={L.catBlocked} value={preview.blocked} tone="border-amber-500/40 bg-amber-500/10 text-amber-600" testid="catalog-stat-blocked" />
          </div>

          {preview.examples?.length > 0 && (
            <div className="rounded-xl border border-border">
              <div className="border-b border-border px-3 py-2 text-xs font-semibold uppercase text-muted-foreground">{L.catExamples}</div>
              <div className="max-h-64 overflow-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {preview.examples.map((e) => (
                      <tr key={e.service_id} className="border-t border-border/50 first:border-t-0" data-testid={`catalog-example-${e.service_id}`}>
                        <td className="max-w-[300px] py-2 pl-3"><div className="truncate" title={e.name}>{e.name}</div>
                          <div className="text-[11px] text-muted-foreground">{L.provider}: {e.provider || L.catNone} · <SourceBadge s={e.pricing_source} L={L} /></div></td>
                        <td className="whitespace-nowrap py-2 pr-3 text-right font-medium">
                          <span className="text-muted-foreground">{money(e.current_price_per_1000)}</span>
                          <span className="mx-1 text-primary">→</span>
                          <span className="text-primary">{money(e.proposed_price_per_1000)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {preview.blocked_details?.length > 0 && (
            <div className="rounded-xl border border-amber-500/30">
              <div className="flex items-center gap-1.5 border-b border-amber-500/30 px-3 py-2 text-xs font-semibold uppercase text-amber-600">
                <ShieldAlert className="h-3.5 w-3.5" /> {L.catBlockedList}</div>
              <div className="max-h-48 overflow-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {preview.blocked_details.map((b) => (
                      <tr key={b.service_id} className="border-t border-border/50 first:border-t-0" data-testid={`catalog-blocked-${b.service_id}`}>
                        <td className="max-w-[300px] py-2 pl-3"><div className="truncate" title={b.name}>{b.name}</div></td>
                        <td className="py-2 pr-3 text-right"><span className="rounded bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-600">{b.reason}</span>
                          <div className="text-[11px] text-muted-foreground">{money(b.current_price_per_1000)}</div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {preview.would_change > 0 ? (
            <div className="flex flex-col gap-3 border-t border-border pt-3 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label className="mb-1 block text-xs text-muted-foreground">{L.catReason}</label>
                <input data-testid="pricing-catalog-reason" value={reason} onChange={(ev) => setReason(ev.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
              </div>
              <button data-testid="pricing-catalog-apply-btn" onClick={runApply} disabled={applying}
                className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {applying ? L.catApplying : L.catConfirm}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 border-t border-border pt-3 text-sm text-muted-foreground" data-testid="pricing-catalog-nochange">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" /> {L.catNoChange}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}
